import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useQueryParam } from '../utils/use-query-param';

import { useAuthentication } from './auth-provider';
import { validateRedirect } from './auth-utils';
import { fetchVerificationStatus, VerificationMethod } from './verification-client';

type VerificationStatus = {
  hasEmail: boolean | null;
  isAccountVerified: boolean | null;
  error: Error | null;
};

const useVerificationStatus = ({
  apiUrl,
  isAuthenticated,
  isLoading,
  isVerificationRoute,
}: {
  apiUrl: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  isVerificationRoute: boolean;
}): VerificationStatus => {
  const [isAccountVerified, setIsAccountVerified] = useState<boolean | null>(null);
  const [hasEmail, setHasEmail] = useState<boolean | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    setIsAccountVerified(null);
    setHasEmail(null);

    fetchVerificationStatus(apiUrl)
      .then((response) => {
        setIsAccountVerified(response.isAccountVerified);
        setHasEmail(response.hasEmail);
      })
      .catch((err) => setError(new Error(String(err))));
  }, [apiUrl, isAuthenticated, isLoading, isVerificationRoute]);

  return { isAccountVerified, hasEmail, error };
};

const defaultResolveMethod = (): VerificationMethod => 'email';

const getSetupUrl = (method: VerificationMethod, redirect: string) => {
  const params = new URLSearchParams({ redirect, method });
  return `/account-verification?${params.toString()}`;
};

const getVerifiedRedirect = (redirectParam: string | undefined, redirectWhitelist: string[] | undefined) =>
  validateRedirect(redirectParam, redirectWhitelist) ?? '/';

const getUnverifiedRedirect = ({
  hasEmail,
  pathname,
  requireEmail,
  resolveMethod,
}: {
  hasEmail: boolean | null;
  pathname: string;
  requireEmail: boolean;
  resolveMethod: (email: boolean | null) => VerificationMethod;
}) => {
  if (requireEmail && !hasEmail) {
    throw new Error('Cannot verify account: authenticated user has no email address');
  }
  return getSetupUrl(resolveMethod(hasEmail), pathname);
};

const getRedirect = ({
  hasEmail,
  isAccountVerified,
  isAuthenticated,
  isLoading,
  isVerificationRoute,
  pathname,
  redirectParam,
  redirectWhitelist,
  requireEmail,
  resolveMethod,
}: {
  hasEmail: boolean | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAccountVerified: boolean | null;
  isVerificationRoute: boolean;
  pathname: string;
  redirectParam?: string;
  redirectWhitelist?: string[];
  requireEmail: boolean;
  resolveMethod: (email: boolean | null) => VerificationMethod;
}) => {
  if (!isAuthenticated || isLoading || isAccountVerified === null) return null;
  if (isAccountVerified && isVerificationRoute) return getVerifiedRedirect(redirectParam, redirectWhitelist);
  if (!isAccountVerified && !isVerificationRoute) {
    return getUnverifiedRedirect({ hasEmail, pathname, requireEmail, resolveMethod });
  }
  return null;
};

type RequireVerificationProps = {
  readonly redirectWhitelist?: string[];
  readonly resolveMethod?: (email: boolean | null) => VerificationMethod;
  readonly requireEmail?: boolean;
};

const RequireVerification = ({
  redirectWhitelist,
  resolveMethod = defaultResolveMethod,
  requireEmail = false,
}: RequireVerificationProps) => {
  const { apiUrl, isAuthenticated, isLoading } = useAuthentication();
  const { pathname } = useLocation();
  const redirectParam = useQueryParam('redirect');
  const isVerificationRoute = pathname === '/account-verification';
  const { isAccountVerified, hasEmail, error } = useVerificationStatus({
    apiUrl,
    isAuthenticated,
    isLoading,
    isVerificationRoute,
  });
  const isPending = !isAuthenticated || isLoading || isAccountVerified === null;
  const redirectTo = useMemo(() => {
    if (error) return null;
    return getRedirect({
      hasEmail,
      isAccountVerified,
      isAuthenticated,
      isLoading,
      isVerificationRoute,
      pathname,
      redirectParam,
      redirectWhitelist,
      requireEmail,
      resolveMethod,
    });
  }, [
    error,
    hasEmail,
    isAccountVerified,
    isAuthenticated,
    isLoading,
    isVerificationRoute,
    pathname,
    redirectParam,
    redirectWhitelist,
    requireEmail,
    resolveMethod,
  ]);

  if (error) throw error;
  if (isPending) return null;
  if (redirectTo) {
    return <Navigate replace to={redirectTo} />;
  }
  return <Outlet />;
};

export { RequireVerification, useVerificationStatus };
